import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  styled,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormHelperText,
  LinearProgress,
  TableSortLabel,
  TablePagination,
  Collapse,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { db, storage } from '../firebase';
import { collection, getDocs, doc, updateDoc, addDoc, query, orderBy, limit, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import CustomAudioPlayer from './CustomAudioPlayer';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { createAudioPreview } from '../utils/ffmpeg';
import config from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

// Replace all baseURL usages with config.storage.baseURL
const baseURL = config.storage.baseURL;

// Interface for Track data
interface Track {
  id: string;
  name: string;
  is_active: boolean;
  price: number;
  is_free: boolean;
  track_url?: string;
  preview_url?: string;
  created_at: number;
  updated_at: number;
  image?: string;
  product_id?: number;
}

// Interface for Firestore user data
interface FirestoreUser {
  first_name: string;
  last_name: string;
  email: string;
}

// Add these interfaces after the existing Track interface
interface FormData {
  title: string;
  artist: string;
  category: string;
  subCategory: string;
  language: string;
  image: File | string | null;
  [key: string]: string | File | null;
}

interface FormErrors {
  trackName: string;
  price: string;
  uploadTrack: string;
}

// Update the Snackbar interface
interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

// Styled Components
const MainContent = styled(Box)({
  padding: '0px 10px',
  backgroundColor: '#ffffff',
  '@media (max-width: 1024px)': {
    padding: '0px 16px',
  }
});

const FormContainer = styled(Paper)({
  padding: '24px',
  backgroundColor: '#ffd999',
  borderRadius: '8px',
  width: '100%',
  '& .MuiTextField-root': {
    backgroundColor: '#ffffff'
  },
  '@media (max-width: 1024px)': {
    padding: '20px',
  }
});

const StyledTableContainer = styled(TableContainer)({
  marginTop: '16px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  overflowX: 'auto',
  position: 'relative',
  '@media (max-width: 1024px)': {
    maxWidth: '100vw',
    minWidth: '600px',
    padding: 0,
  },
  '& .MuiTable-root': {
    minWidth: '800px',
    '@media (max-width: 1024px)': {
      minWidth: '600px',
    },
  },
  '& .MuiTableCell-head': {
    backgroundColor: '#ffd999',
    fontWeight: 'bold',
    color: '#000000',
    padding: '8px 8px',
    border: '1px solid #e0e0e0',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
    position: 'relative',
    '@media (max-width: 1024px)': {
      padding: '6px 6px',
      fontSize: '0.95rem',
    }
  },
  '& .MuiTableCell-body': {
    padding: '8px 8px',
    border: '1px solid #e0e0e0',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    position: 'relative',
    '@media (max-width: 1024px)': {
      padding: '6px 6px',
      fontSize: '0.95rem',
    }
  },
  '& .track-cell': {
    width: '100px',
    maxWidth: '100px',
    padding: '4px 4px',
    '@media (max-width: 1024px)': {
      width: '80px',
      maxWidth: '80px',
      padding: '4px',
    }
  }
});

const AddButton = styled(Button)({
  backgroundColor: '#2196F3',
  color: 'white',
  '&:hover': {
    backgroundColor: '#1976D2',
  },
  textTransform: 'none',
  minWidth: '120px',
  '@media (max-width: 1024px)': {
    minWidth: '140px',
    padding: '10px 20px',
    fontSize: '1rem'
  }
});

const VisuallyHiddenInput = styled('input')({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  opacity: 0,
  cursor: 'pointer',
  zIndex: 1,
  '-webkit-appearance': 'none',
  appearance: 'none',
  '&::-webkit-file-upload-button': {
    visibility: 'hidden',
  },
  '&::file-selector-button': {
    visibility: 'hidden',
  }
});

const AudioPlayerContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#ffffff',
  padding: '8px 16px',
  borderRadius: '4px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  marginLeft: '16px',
  '& audio': {
    height: '32px',
    '@media (max-width: 1024px)': {
      height: '40px',
      width: '100%'
    }
  }
});

const SearchTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#2196F3',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2196F3',
    },
    '@media (max-width: 1024px)': {
      fontSize: '1rem',
      '& input': {
        padding: '12px 14px',
      }
    }
  },
  '@media (max-width: 1024px)': {
    width: '100%',
    maxWidth: '400px'
  }
});

const ActionButton = styled(Button)({
  backgroundColor: 'transparent',
  color: '#FF7043',
  '&:hover': {
    textDecoration: 'underline'
  },
  textTransform: 'none',
  minWidth: 'auto',
  fontSize: '12px'
});

const HighlightedRow = styled(TableRow)(({ theme }) => ({
  animation: 'highlight 5s ease-out',
  '@keyframes highlight': {
    '0%': {
      backgroundColor: '#ffd999',
      transform: 'scale(1.02)',
    },
    '50%': {
      backgroundColor: '#ffd999',
      transform: 'scale(1.02)',
    },
    '100%': {
      backgroundColor: 'transparent',
      transform: 'scale(1)',
    },
  },
}));

const StatusToggle = styled(Box)({
  width: '40px',
  height: '20px',
  backgroundColor: '#ccc',
  borderRadius: '20px',
  display: 'inline-block',
  position: 'relative',
  cursor: 'pointer',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '16px',
    height: '16px',
    backgroundColor: '#fff',
    borderRadius: '50%',
    top: '2px',
    left: '2px',
    transition: 'left 0.2s'
  },
  '@media (max-width: 1024px)': {
    width: '48px',
    height: '24px',
    '&::after': {
      width: '20px',
      height: '20px',
      top: '2px',
      left: '2px'
    }
  }
});

export default function ManageTracks() {
  const { currentUser } = useAuth();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [trackName, setTrackName] = useState('');
  const [isFree, setIsFree] = useState(true);
  const [price, setPrice] = useState('');
  const [editingTrack, setEditingTrack] = useState<Track | null>(null);
  const [deactivateDialog, setDeactivateDialog] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [errors, setErrors] = useState<FormErrors>({
    trackName: '',
    price: '',
    uploadTrack: ''
  });
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewUploadProgress, setPreviewUploadProgress] = useState(0);
  const [orderByField, setOrderByField] = useState<keyof Track>('updated_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});
  const [highlightedTrackId, setHighlightedTrackId] = useState<string | null>(null);
  const [highlightedCardId, setHighlightedCardId] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState('');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    artist: '',
    category: '',
    subCategory: '',
    language: '',
    image: null
  });
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [currentFileExtension, setCurrentFileExtension] = useState<string>('');
  const [originalTrackData, setOriginalTrackData] = useState<Track | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Add refs for file inputs
  const audioInputRef = useRef<HTMLInputElement>(null);

  // Function to check if there are any changes
  const checkForChanges = () => {
    if (!originalTrackData || !editingTrack) {
      setHasChanges(false);
      return;
    }

    const currentPrice = isFree ? 0 : parseFloat(price || '0');

    const changes = [
      trackName !== originalTrackData.name,
      isFree !== originalTrackData.is_free,
      currentPrice !== originalTrackData.price,
      audioFile !== null, // New audio file selected
      previewUrl !== originalTrackData.preview_url // Check for preview URL changes
    ];

    setHasChanges(changes.some(change => change));
  };

  // Define showSnackbar and handleCloseSnackbar functions once at the top
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchTracks();
    // Cleanup audio player on component unmount
    return () => {
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.src = '';
      }
    };
  }, []);

  useEffect(() => {
    if (highlightedTrackId) {
      const timer = setTimeout(() => {
        setHighlightedTrackId(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightedTrackId]);

  useEffect(() => {
    if (highlightedCardId) {
      const timer = setTimeout(() => {
        setHighlightedCardId(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightedCardId]);

  // Check for changes whenever form data changes
  useEffect(() => {
    checkForChanges();
  }, [trackName, isFree, price, audioFile, previewUrl]);

  const getNextProductId = async (): Promise<number> => {
    try {
      const tracksRef = collection(db, 'music_tracks');
      const q = query(tracksRef, orderBy('product_id', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return 1; // If no tracks exist, start with 1
      }
      
      const lastTrack = querySnapshot.docs[0].data();
      const lastProductId = lastTrack.product_id || 0;
      
      // If the last track doesn't have a product_id, we need to handle this case
      if (lastProductId === 0) {
        // Get all tracks and find the highest existing product_id
        const allTracksQuery = query(tracksRef);
        const allTracksSnapshot = await getDocs(allTracksQuery);
        let maxProductId = 0;
        
        allTracksSnapshot.docs.forEach(doc => {
          const trackData = doc.data();
          if (trackData.product_id && trackData.product_id > maxProductId) {
            maxProductId = trackData.product_id;
          }
        });
        
        return maxProductId + 1;
      }
      
      return lastProductId + 1;
    } catch (error) {
      console.error('Error getting next product ID:', error);
      throw error;
    }
  };

  const fetchTracks = async () => {
    try {
      setLoading(true);
      const tracksRef = collection(db, 'music_tracks');
      // Always sort by updated_at in descending order
      const q = query(tracksRef, orderBy('updated_at', 'desc'));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log('No tracks found in Firestore');
        setTracks([]);
        return;
      }

      const tracksData = snapshot.docs.map(doc => {
        const data = doc.data();
        // Convert Firestore timestamps to milliseconds for consistent sorting
        const updatedAt = data.updated_at?.toDate?.()?.getTime() || 0;
        const createdAt = data.created_at?.toDate?.()?.getTime() || updatedAt;
        
        return {
          id: doc.id,
          name: data.name || '',
          is_active: data.is_active ?? true,
          price: data.price || 0,
          is_free: data.is_free ?? true,
          track_url: data.track_url || '',
          preview_url: data.preview_url || '',
          created_at: createdAt,
          updated_at: updatedAt,
          product_id: data.product_id || 0
        };
      });

      // Check if any tracks need product_id assignment
      const tracksWithoutProductId = tracksData.filter(track => track.product_id === 0);
      if (tracksWithoutProductId.length > 0) {
        console.log(`Found ${tracksWithoutProductId.length} tracks without product_id, attempting to assign...`);
        await assignProductIdsToExistingTracks(tracksWithoutProductId);
        // Refresh the tracks after assignment
        await fetchTracks();
        return;
      }

      // Set tracks without additional sorting since Firestore already sorts them
      setTracks(tracksData);
      setPage(0);
    } catch (error) {
      console.error('Error fetching tracks:', error);
      showSnackbar('Error fetching tracks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const assignProductIdsToExistingTracks = async (tracksWithoutProductId: Track[]) => {
    try {
      // Get the next available product_id
      const nextProductId = await getNextProductId();
      
      // Update each track with a product_id
      for (let i = 0; i < tracksWithoutProductId.length; i++) {
        const track = tracksWithoutProductId[i];
        const trackRef = doc(db, 'music_tracks', track.id);
        
        await updateDoc(trackRef, {
          product_id: nextProductId + i
        });
        
        console.log(`Assigned product_id ${nextProductId + i} to track "${track.name}"`);
      }
      
      showSnackbar(`Successfully assigned product IDs to ${tracksWithoutProductId.length} tracks`, 'success');
    } catch (error) {
      console.error('Error assigning product IDs to existing tracks:', error);
      showSnackbar('Error assigning product IDs to existing tracks', 'error');
    }
  };

  const uploadAudio = async (file: File): Promise<{ mainUrl: string; previewUrl: string | null }> => {
    try {
      console.log('Starting audio upload process...');
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });
      console.log('Current environment:', config.environment);
      console.log('Storage bucket:', config.firebase.storageBucket);
      console.log('Base URL:', baseURL);
      
      // Check authentication status
      console.log('Current user:', currentUser);
      console.log('User authenticated:', !!currentUser);
      console.log('User ID:', currentUser?.uid);
      
      if (!currentUser) {
        const errorMessage = 'User not authenticated. Please log in again.';
        console.error(errorMessage);
        setErrors(prev => ({ ...prev, uploadTrack: errorMessage }));
        showSnackbar(errorMessage, 'error');
        throw new Error(errorMessage);
      }
      
      setUploadProgress(0);
      // Preserve the original file extension
      const fileExtension = file.name.slice(file.name.lastIndexOf('.'));
      const fileName = file.name.substring(0, file.name.lastIndexOf('.'));
      
      console.log('File path components:', { fileName, fileExtension });
      
      // Start both uploads in parallel
      const mainUploadPromise = new Promise<string>((resolve, reject) => {
        const storagePath = `Music_Files/${fileName}${fileExtension}`;
        console.log('Main upload storage path:', storagePath);
        
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload progress:', progress);
            setUploadProgress(progress);
            showSnackbar(`Uploading: ${Math.round(progress)}%`, 'info');
          },
          (error) => {
            console.error('Error uploading audio:', error);
            console.error('Error details:', {
              code: error.code,
              message: error.message,
              serverResponse: error.serverResponse,
              name: error.name
            });
            const errorMessage = `Error uploading audio file: ${error.message}`;
            setErrors(prev => ({ ...prev, uploadTrack: errorMessage }));
            showSnackbar(errorMessage, 'error');
            setAudioUrl('');
            reject(error);
          },
          async () => {
            try {
              console.log('Main upload completed, getting download URL...');
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log('Download URL received:', downloadURL);
              const remainingURL = downloadURL.substring(baseURL.length);
              console.log('Remaining URL:', remainingURL);
              setAudioUrl(remainingURL);
              showSnackbar(`${fileName}${fileExtension} uploaded successfully`, 'success');
              resolve(remainingURL);
            } catch (error) {
              console.error('Error getting download URL:', error);
              console.error('Download URL error details:', {
                code: (error as any)?.code,
                message: (error as any)?.message,
                serverResponse: (error as any)?.serverResponse,
                name: (error as any)?.name
              });
              const errorMessage = `Error getting audio URL: ${(error as any)?.message || 'Unknown error'}`;
              setErrors(prev => ({ ...prev, uploadTrack: errorMessage }));
              showSnackbar(errorMessage, 'error');
              setAudioUrl('');
              reject(error);
            }
          }
        );
      });

      // Create and upload preview in parallel
      const previewPromise = createAudioPreview(file).then(async (previewFile) => {
        console.log('Preview file created:', {
          name: previewFile.name,
          size: previewFile.size,
          type: previewFile.type
        });
        
        // Compress the preview file if it's too large
        const compressedPreview = await compressAudioFile(previewFile);
        console.log('Compressed preview file:', {
          name: compressedPreview.name,
          size: compressedPreview.size,
          type: compressedPreview.type
        });
        
        const previewStoragePath = `preview_tracks/${fileName}_preview.mp3`;
        console.log('Preview upload storage path:', previewStoragePath);
        
        const previewStorageRef = ref(storage, previewStoragePath);
        const previewUploadTask = uploadBytesResumable(previewStorageRef, compressedPreview);

        return new Promise<string>((resolve, reject) => {
          previewUploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log('Preview upload progress:', progress);
              setPreviewUploadProgress(progress);
            },
            (error) => {
              console.error('Error uploading preview:', error);
              console.error('Preview upload error details:', {
                code: error.code,
                message: error.message,
                serverResponse: error.serverResponse,
                name: error.name
              });
              showSnackbar(`Error creating preview: ${error.message}`, 'error');
              reject(error);
            },
            async () => {
              try {
                console.log('Preview upload completed, getting download URL...');
                const previewDownloadURL = await getDownloadURL(previewUploadTask.snapshot.ref);
                console.log('Preview download URL received:', previewDownloadURL);
                const previewRemainingURL = previewDownloadURL.substring(baseURL.length);
                console.log('Preview remaining URL:', previewRemainingURL);
                setPreviewUrl(previewRemainingURL);
                showSnackbar('Preview created successfully', 'success');
                resolve(previewRemainingURL);
                          } catch (error) {
              console.error('Error getting preview URL:', error);
              console.error('Preview download URL error details:', {
                code: (error as any)?.code,
                message: (error as any)?.message,
                serverResponse: (error as any)?.serverResponse,
                name: (error as any)?.name
              });
              showSnackbar(`Error getting preview URL: ${(error as any)?.message || 'Unknown error'}`, 'error');
              reject(error);
            }
            }
          );
        });
      }).catch(error => {
        console.error('Error creating preview:', error);
        console.error('Preview creation error details:', {
          code: (error as any)?.code,
          message: (error as any)?.message,
          name: (error as any)?.name
        });
        showSnackbar(`Error creating preview: ${(error as any)?.message || 'Unknown error'}`, 'error');
        return null;
      });

      // Wait for both uploads to complete
      console.log('Waiting for both uploads to complete...');
      const [mainUrl, previewUrl] = await Promise.all([mainUploadPromise, previewPromise]);
      console.log('Both uploads completed successfully');
      return { mainUrl, previewUrl };
    } catch (error) {
      console.error('Error in uploadAudio:', error);
      console.error('Upload audio error details:', {
        code: (error as any)?.code,
        message: (error as any)?.message,
        serverResponse: (error as any)?.serverResponse,
        name: (error as any)?.name,
        stack: (error as any)?.stack
      });
      const errorMessage = `Error uploading audio file: ${(error as any)?.message || 'Unknown error'}`;
      setErrors(prev => ({ ...prev, uploadTrack: errorMessage }));
      showSnackbar(errorMessage, 'error');
      setAudioUrl('');
      throw error;
    }
  };

  // Add compression function
  const compressAudioFile = async (file: File): Promise<File> => {
    // If file is already small enough, return as is
    if (file.size <= 2 * 1024 * 1024) { // 2MB
      return file;
    }

    try {
      const ffmpeg = new FFmpeg();
      await ffmpeg.load();

      // Write the file to FFmpeg's virtual file system
      await ffmpeg.writeFile('input.mp3', await fetchFile(file));

      // Compress the audio with lower bitrate
      await ffmpeg.exec([
        '-i', 'input.mp3',
        '-b:a', '96k', // Lower bitrate for preview
        '-ac', '2',    // Stereo
        '-ar', '44100', // Sample rate
        'output.mp3'
      ]);

      // Read the compressed file
      const data = await ffmpeg.readFile('output.mp3');
      const compressedBlob = new Blob([data], { type: 'audio/mp3' });
      
      // Create a new File object
      return new File([compressedBlob], file.name.replace(/\.[^/.]+$/, '_compressed.mp3'), {
        type: 'audio/mp3'
      });
    } catch (error) {
      console.error('Error compressing audio:', error);
      // If compression fails, return original file
      return file;
    }
  };

  const handleUploadTrack = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    
    // Clear any previous errors
    setErrors(prev => ({ ...prev, uploadTrack: '' }));
    
    if (!file) {
      return;
    }

    // Get file extension
    const fileExtension = file.name.toLowerCase().split('.').pop() || '';
    setCurrentFileExtension(fileExtension);

    // File size validation removed - allowing any size

    // Check for corrupted audio file
    try {
      const audio = new Audio();
      audio.src = URL.createObjectURL(file);
      await new Promise((resolve, reject) => {
        audio.oncanplaythrough = resolve;
        audio.onerror = reject;
      });
      URL.revokeObjectURL(audio.src);
    } catch (error) {
      setErrors(prev => ({ ...prev, uploadTrack: 'Corrupt audio file. Please try another file.' }));
      showSnackbar('Corrupt audio file. Please try another file.', 'error');
      input.value = '';
      return;
    }

    // Validate file type
    const allowedTypes = [
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/x-m4a',
      'audio/m4a',
      'audio/ogg',
      'audio/x-wav'
    ];

    const isValidType = allowedTypes.includes(file.type) || 
                       (fileExtension && ['mp3', 'wav', 'm4a', 'ogg'].includes(fileExtension));

    if (!isValidType) {
      setErrors(prev => ({ ...prev, uploadTrack: 'File must be MP3, WAV, M4A, or OGG format' }));
      showSnackbar('File must be MP3, WAV, M4A, or OGG format', 'error');
      input.value = '';
      return;
    }

    // Handle filename length
    const originalName = file.name.substring(0, file.name.lastIndexOf('.'));
    const extension = file.name.substring(file.name.lastIndexOf('.'));
    let trimmedName = originalName;
    if (originalName.length > 50) {
      trimmedName = originalName.substring(0, 50);
      try {
        const newFile = new File([file], trimmedName + extension, { type: file.type });
        setAudioFile(newFile);
      } catch (err) {
        setErrors(prev => ({ ...prev, uploadTrack: 'Browser does not support renaming files for upload.' }));
        showSnackbar('File name too long and cannot be trimmed. Please use a different file.', 'error');
        input.value = '';
        return;
      }
    } else {
      setAudioFile(file);
    }

    // Clear error as soon as a valid file is selected
    setErrors(prev => ({ ...prev, uploadTrack: '' }));

    // Set track name from file name (without extension)
    setTrackName(trimmedName);
    // Clear track name error if now valid
    if (trimmedName.trim()) {
      setErrors(prev => ({ ...prev, trackName: '' }));
    }

    setUploadProgress(0);
    setPreviewUploadProgress(0);

    try {
      const { mainUrl, previewUrl } = await uploadAudio(file);
      setAudioUrl(mainUrl);
      setPreviewUrl(previewUrl || '');
    } catch (error) {
      console.error('Error in handleUploadTrack:', error);
      showSnackbar('Error uploading track', 'error');
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleToggleStatus = async (track: Track) => {
    if (track.is_active && !track.is_free) {
      // If trying to deactivate a paid track, show confirmation dialog
      setSelectedTrack(track);
      setDeactivateDialog(true);
    } else {
      // If activating or deactivating a free track, proceed without confirmation
      await updateTrackStatus(track);
    }
  };

  const updateTrackStatus = async (track: Track) => {
    try {
      const trackRef = doc(db, 'music_tracks', track.id);
      await updateDoc(trackRef, {
        is_active: !track.is_active,
        last_modified: new Date().toISOString()
      });
      
      // Update local state
      setTracks(tracks.map(t => 
        t.id === track.id 
          ? { ...t, is_active: !t.is_active, last_modified: new Date().toISOString() }
          : t
      ));

      // Show success message
      showSnackbar(`Track ${!track.is_active ? 'activated' : 'deactivated'} successfully`, 'success');
    } catch (error) {
      console.error('Error updating track status:', error);
      showSnackbar('Error updating track status', 'error');
    }
  };

  const handleConfirmDeactivate = async () => {
    if (selectedTrack) {
      await updateTrackStatus(selectedTrack);
    }
    setDeactivateDialog(false);
    setSelectedTrack(null);
  };

  const handleCancelDeactivate = () => {
    setDeactivateDialog(false);
    setSelectedTrack(null);
  };

  const handleEdit = (track: Track) => {
    setEditingTrack(track);
    setTrackName(track.name);
    setIsFree(track.is_free);
    setPrice(track.is_free ? '' : track.price.toString());
    
    // Set the audio URL correctly for existing tracks
    if (track.track_url) {
      setAudioUrl(track.track_url);
    }
    
    // Set the preview URL correctly for existing tracks
    if (track.preview_url) {
      setPreviewUrl(track.preview_url);
    }
    
    // Store original track data for change detection
    setOriginalTrackData(track);
    setHasChanges(false);
    
    // Expand the form when editing
    setIsFormExpanded(true);

    // Scroll to top of the form
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleTrackNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const newErrors = { ...errors };
    // Truncate input to 50 characters
    const truncatedValue = value.slice(0, 50);
    // Check for empty value
    if (!truncatedValue.trim()) {
      newErrors.trackName = 'Track name is required';
    }
    // Check for leading or trailing space
    else if (truncatedValue.startsWith(' ') || truncatedValue.endsWith(' ')) {
      newErrors.trackName = 'Track name cannot start or end with a space';
    }
    // Check for character limit
    else if (value.length > 50) {
      newErrors.trackName = 'Track name cannot exceed 50 characters';
    }
    // Check for duplicate track name
    else {
      const isDuplicate = tracks.some(track => 
        track.name.toLowerCase() === truncatedValue.toLowerCase() && 
        track.id !== editingTrack?.id
      );
      if (isDuplicate) {
        newErrors.trackName = 'A track with this name already exists';
      } else {
        // Clear the error if all validations pass
        newErrors.trackName = '';
      }
    }
    setErrors(newErrors);
    setTrackName(truncatedValue);
  };

  const handleSave = async () => {
    // Always trim trackName to 50 characters before validation and saving
    const trimmedTrackName = trackName.slice(0, 50);
    if (trimmedTrackName !== trackName) {
      setTrackName(trimmedTrackName);
    }
    // Validate form
    const newErrors = {
      trackName: '',
      price: '',
      uploadTrack: ''
    };
    let hasError = false;
    if (!trimmedTrackName.trim()) {
      newErrors.trackName = 'Track name is required';
      hasError = true;
    }
    // Check for leading or trailing space
    else if (trimmedTrackName.startsWith(' ') || trimmedTrackName.endsWith(' ')) {
      newErrors.trackName = 'Track name cannot start or end with a space';
      hasError = true;
    }
    // Check for duplicate track name
    else {
      const isDuplicate = tracks.some(track => 
        track.name.toLowerCase() === trimmedTrackName.toLowerCase() && 
        track.id !== editingTrack?.id
      );
      if (isDuplicate) {
        newErrors.trackName = 'A track with this name already exists';
        hasError = true;
      }
    }
    if (!isFree) {
      if (!price.trim()) {
        newErrors.price = 'Price is required for paid tracks';
        hasError = true;
      } else if (isNaN(parseFloat(price)) || parseFloat(price) <= 0) {
        newErrors.price = 'Please enter a valid price';
        hasError = true;
      }
    }
    // Add validation for track upload
    if (!audioUrl && !editingTrack?.track_url) {
      newErrors.uploadTrack = 'Please upload a track file';
      hasError = true;
    }
    setErrors(newErrors);
    if (hasError) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    try {
      setSaving(true);
      setErrors({
        trackName: '',
        price: '',
        uploadTrack: ''
      });

      // Create a new track document reference
      const trackRef = editingTrack 
        ? doc(db, 'music_tracks', editingTrack.id)
        : doc(collection(db, 'music_tracks'));

      // Prepare track data with serverTimestamp
      const trackData = {
        name: trimmedTrackName,
        track_url: audioUrl,
        preview_url: previewUrl,
        updated_at: serverTimestamp(),
        is_free: isFree,
        price: isFree ? 0 : parseFloat(price),
        is_active: true
      };

      if (editingTrack) {
        // Update existing track - preserve existing product_id
        const existingTrack = tracks.find(t => t.id === editingTrack.id);
        const updateData = {
          ...trackData,
          product_id: existingTrack?.product_id || editingTrack.product_id || 0,
          updated_at: serverTimestamp()
        };
        await updateDoc(trackRef, updateData);
      } else {
        // Create new track with both timestamps and product_id
        const nextProductId = await getNextProductId();
        await setDoc(trackRef, {
          ...trackData,
          product_id: nextProductId,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        });
      }

      // Wait for server timestamp to be resolved
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Always fetch fresh data after any update
      await fetchTracks();
      
      if (editingTrack) {
        showSnackbar('Track updated successfully!', 'success');
        setHighlightedTrackId(editingTrack.id);
      } else {
        showSnackbar('Track saved successfully!', 'success');
        // Set highlightedTrackId for the newly added track
        setHighlightedTrackId(trackRef.id);
      }

      // Reset form
      setFormData({
        title: '',
        artist: '',
        category: '',
        subCategory: '',
        language: '',
        image: null
      });
      setTrackName('');
      setAudioUrl('');
      setPreviewUrl('');
      setAudioFile(null);
      setImageUploadProgress(0);
      setPreviewUploadProgress(0);
      setUploadProgress(0);
      setIsFree(true);
      setPrice('');
      setEditingTrack(null);
      setIsFormExpanded(false);
      setSaving(false);

    } catch (error) {
      console.error('Error saving track:', error);
      showSnackbar('Error saving track', 'error');
      setSaving(false);
    }
  };

  // Update handlePriceChange to clear validation messages
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const newErrors = { ...errors };
    
    // Only allow numbers and a single decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      // Prevent multiple decimal points
      if ((value.match(/\./g) || []).length <= 1) {
        // Limit to 2 decimal places
        if (value.includes('.')) {
          const decimalPlaces = value.split('.')[1];
          if (decimalPlaces.length <= 2) {
            // Check if the value is between 0 and 9999.99
            const numericValue = parseFloat(value);
            if (isNaN(numericValue)) {
              setPrice(value);
              newErrors.price = ''; // Clear error for empty or partial input
            } else if (numericValue < 0) {
              newErrors.price = 'Price cannot be negative';
            } else if (numericValue > 9999.99) {
              newErrors.price = 'Price cannot exceed $9999.99';
            } else {
              setPrice(value);
              newErrors.price = ''; // Clear error for valid price
            }
          }
        } else {
          // For whole numbers, check if the value is between 0 and 9999.99
          const numericValue = parseFloat(value);
          if (isNaN(numericValue)) {
            setPrice(value);
            newErrors.price = ''; // Clear error for empty or partial input
          } else if (numericValue < 0) {
            newErrors.price = 'Price cannot be negative';
          } else if (numericValue > 9999.99) {
            newErrors.price = 'Price cannot exceed $9999.99';
          } else {
            setPrice(value);
            newErrors.price = ''; // Clear error for valid price
          }
        }
      }
    }
    setErrors(newErrors);
  };

  const handleReset = () => {
    setEditingTrack(null);
    setTrackName('');
    setIsFree(true);
    setPrice('');
    setAudioUrl('');
    setPreviewUrl('');
    setCurrentFileExtension('');
    setOriginalTrackData(null);
    setHasChanges(false);
    setErrors({ trackName: '', price: '', uploadTrack: '' });
    if (audioInputRef.current) {
      audioInputRef.current.value = '';
    }
    // Close the collapsible section
    setIsFormExpanded(false);
  };

  // Update filteredTracks to maintain Firestore's sorting order
  const filteredTracks = tracks
    .filter(track => 
      track.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (orderByField === 'name') {
        return order === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (orderByField === 'price') {
        const priceA = Number(a.price) || 0;
        const priceB = Number(b.price) || 0;
        return order === 'asc' 
          ? priceA - priceB
          : priceB - priceA;
      } else if (orderByField === 'updated_at') {
        // For updated_at, use the timestamp directly
        return order === 'asc' 
          ? a.updated_at - b.updated_at
          : b.updated_at - a.updated_at;
      }
      // Default to Firestore's order (updated_at desc)
      return b.updated_at - a.updated_at;
    });

  // Add handleRequestSort function
  const handleRequestSort = (property: keyof Track) => {
    const isAsc = orderByField === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderByField(property);
  };

  // Add pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewImage = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage('');
  };

  // Add audio player handlers
  const handleAudioPlay = (trackId: string) => {
    // Pause any currently playing audio (including form audio)
    if (currentlyPlayingId && currentlyPlayingId !== trackId) {
      const previousAudio = audioRefs.current[currentlyPlayingId];
      if (previousAudio) {
        previousAudio.pause();
      }
    }
    // If form audio was playing, pause it
    if (currentlyPlayingId === 'form-edit') {
      // The form audio will be paused by the CustomAudioPlayer component
    }
    setCurrentlyPlayingId(trackId);
  };

  const handleAudioPause = (trackId: string) => {
    if (currentlyPlayingId === trackId) {
      setCurrentlyPlayingId(null);
    }
  };

  // Form audio player handlers
  const handleFormAudioPlay = () => {
    // Pause any currently playing audio from table
    if (currentlyPlayingId) {
      const previousAudio = audioRefs.current[currentlyPlayingId];
      if (previousAudio) {
        previousAudio.pause();
      }
      setCurrentlyPlayingId(null);
    }
    // Set form as currently playing
    setCurrentlyPlayingId('form-edit');
  };

  const handleFormAudioPause = () => {
    if (currentlyPlayingId === 'form-edit') {
      setCurrentlyPlayingId(null);
    }
  };

  // Add cleanup for audio players
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  // Add handler for volume change (mute/unmute)
  const handleAudioVolumeChange = (trackId: string, event: Event) => {
    const audio = event.target as HTMLAudioElement;
    if (audio.muted) {
      audio.pause();
      handleAudioPause(trackId);
    }
  };

  // Add ref callback to set up audio event listeners
  const setupAudioRef = (trackId: string, audio: HTMLAudioElement | null) => {
    if (audio) {
      audioRefs.current[trackId] = audio;
      audio.onvolumechange = (e) => handleAudioVolumeChange(trackId, e);
    }
  };

  const renderAudioPlayer = (track: Track) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
      <Tooltip 
        title={`Play preview of ${track.name}`}
        placement="top"
        arrow
        sx={{
          '& .MuiTooltip-tooltip': {
            backgroundColor: '#FF7043',
            color: '#ffffff',
            fontSize: '14px',
            padding: '8px 16px',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }
        }}
      >
        <CustomAudioPlayer
          src={baseURL + (track.preview_url || track.track_url)}
          isPlaying={currentlyPlayingId === track.id}
          onPlay={() => handleAudioPlay(track.id)}
          onPause={() => handleAudioPause(track.id)}
          tooltipTitle={`Play preview of ${track.name}`}
          showTimeline={false}
        />
      </Tooltip>
    </Box>
  );

  return (
    <MainContent>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          onClick={() => setIsFormExpanded(!isFormExpanded)}
          sx={{
            color: '#424242',
            textTransform: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            justifyContent: 'flex-start',
            padding: '4px 12px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            backgroundColor: '#ffd999',
            '&:hover': {
              backgroundColor: '#ffd999'
            }
          }}
        >
          {isFormExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          <Typography variant="h6" sx={{ color: '#424242', fontWeight: 'bold', fontSize: '1.5rem' }}>
            {editingTrack ? 'Edit Track' : 'Add New Track'}
          </Typography>
        </Button>
      </Box>

      <Collapse in={isFormExpanded} sx={{ backgroundColor: '#ffd999', borderRadius: '4px' }}>
        <FormContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 0 }}>
            {/* Track Name */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Typography sx={{ width: '120px', color: '#000000', mt: 1 }}>Track Name :</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  size="small"
                  value={trackName}
                  onChange={handleTrackNameChange}
                  sx={{ 
                    width: '300px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#2196F3',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2196F3',
                      },
                    }
                  }}
                  error={!!errors.trackName}
                />
                {errors.trackName && (
                  <FormHelperText error sx={{ ml: 0 }}>
                    {errors.trackName}
                  </FormHelperText>
                )}
              </Box>
            </Box>

            {/* Upload Track Button & Success Message */}
            <Box sx={{ display: 'flex', alignItems: 'center', ml: '120px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <input
                  id="track-upload"
                  type="file"
                  accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*"
                  onChange={handleUploadTrack}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="contained"
                  component="span"
                  sx={{
                    backgroundColor: '#2196F3',
                    color: 'white',
                    '&:hover': { backgroundColor: '#1976D2' },
                    textTransform: 'none',
                    width: '120px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => document.getElementById('track-upload')?.click()}
                >
                  Upload Track
                </Button>
                {errors.uploadTrack && (
                  <Typography sx={{ color: '#f44336', fontSize: '13px', mt: 1, ml: 0 }}>
                    {errors.uploadTrack}
                  </Typography>
                )}
              </Box>
              {(audioUrl || (editingTrack?.track_url)) && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                  <Typography sx={{ 
                    fontSize: '12px', 
                    color: '#4CAF50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mr: 2
                  }}>
                    ✓ {trackName}{currentFileExtension ? '.' + currentFileExtension : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CustomAudioPlayer
                      src={baseURL + (audioUrl || editingTrack?.track_url)}
                      isPlaying={currentlyPlayingId === 'form-edit'}
                      onPlay={handleFormAudioPlay}
                      onPause={handleFormAudioPause}
                      tooltipTitle={trackName}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {/* Upload Progress */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, minWidth: 200 }}>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                <Typography sx={{ fontSize: 13, minWidth: 60 }}>{`Uploading: ${Math.round(uploadProgress)}%`}</Typography>
              </Box>
            )}

            {/* Pricing Options */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Typography sx={{ minWidth: '120px', fontSize: '14px', mt: 1 }}>Pricing :</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <RadioGroup
                  value={isFree ? 'Free' : 'Paid'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setIsFree(value === 'Free');
                    if (value === 'Free') {
                      setPrice('');
                      setErrors({ ...errors, price: '' });
                    }
                  }}
                >
                  <FormControlLabel 
                    value="Free" 
                    control={<Radio />} 
                    label="Free" 
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel 
                      value="Paid" 
                      control={<Radio />} 
                      label="Paid" 
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                    />
                    {!isFree && (
                      <TextField
                        required
                        size="small"
                        value={price}
                        error={!!errors.price}
                        onChange={handlePriceChange}
                        sx={{ width: '168px',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white'
                          }
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    )}
                  </Box>
                </RadioGroup>
                {!isFree && errors.price && (
                  <Typography sx={{ 
                    fontSize: '12px', 
                    color: '#f44336',
                    marginLeft: '92px',
                    textAlign: 'left'
                  }}>
                    {errors.price}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Save and Reset Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, ml: '120px' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                  disabled={saving || (!!editingTrack && !hasChanges)}
                sx={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1976D2',
                  },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                      color: '#666',
                      cursor: 'not-allowed'
                    },
                  textTransform: 'none',
                  width: '120px'
                }}
              >
                  {saving ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : editingTrack ? (
                    'Update'
                  ) : (
                    'Save'
                  )}
              </Button>
              <Button
                variant="contained"
                onClick={handleReset}
                sx={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1976D2',
                  },
                  textTransform: 'none',
                  width: '120px'
                }}
              >
                Reset
              </Button>
              </Box>
              {editingTrack && hasChanges && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#FF7043', 
                    fontSize: '12px',
                    fontStyle: 'italic'
                  }}
                >
                  Changes detected
                </Typography>
              )}
            </Box>
          </Box>
        </FormContainer>
      </Collapse>

      {/* Manage Tracks Section */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 3,
          mb: 2
        }}>
          <Typography variant="h6" sx={{ color: '#424242', fontWeight: 'bold', fontSize: '1.3rem' }}>
            Manage Tracks
          </Typography>
          <SearchTextField
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '24px' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                      setPage(0);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <CloseIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: '300px' }}
          />
        </Box>

        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: '100px' }}>Status</TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderByField === 'name'}
                    direction={orderByField === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Track Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left" sx={{ width: '100px' }}>
                  <TableSortLabel
                    active={orderByField === 'price'}
                    direction={orderByField === 'price' ? order : 'asc'}
                    onClick={() => handleRequestSort('price')}
                  >
                    Pricing
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" className="track-cell">Track</TableCell>
                <TableCell align="center" sx={{ width: '100px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress sx={{ color: '#FF7043' }} />
                  </TableCell>
                </TableRow>
              ) : filteredTracks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {searchQuery ? 'No tracks found matching your search' : 'No tracks found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTracks
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((track: Track) => (
                    <TableRow
                      key={track.id}
                            sx={{ 
                        backgroundColor: highlightedTrackId === track.id ? '#FFF9C4' : 'inherit',
                        transition: 'background-color 0.5s',
                      }}
                    >
                      <TableCell align="center" sx={{ fontSize: '12px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Switch
                            checked={track.is_active}
                            onChange={(e) => handleToggleStatus(track)}
                            sx={{
                              '& .MuiSwitch-switchBase': {
                                '&.Mui-checked': {
                                  color: '#FF7043',
                                  '& + .MuiSwitch-track': {
                                    backgroundColor: '#FF7043',
                                  },
                                },
                              },
                              '& .MuiSwitch-track': {
                                backgroundColor: '#ccc',
                              },
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px' }}>{track.name}</TableCell>
                      <TableCell align="left" sx={{ fontSize: '12px' }}>
                        {track.is_free ? 'Free' : `$${track.price.toFixed(2)}`}
                      </TableCell>
                      <TableCell align="center" className="track-cell" sx={{ fontSize: '12px' }}>
                        {track.track_url && renderAudioPlayer(track)}
                      </TableCell>
                      <TableCell className="actions-cell" sx={{ fontSize: '12px' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                          <ActionButton
                            onClick={() => handleEdit(track)}
                            sx={{ 
                              color: '#FF7043',
                              '&:hover': {
                                textDecoration: 'underline'
                              },
                              fontSize: '12px'
                            }}
                          >
                            Edit
                          </ActionButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {/* Add TablePagination after the Table */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTracks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '& .MuiTablePagination-selectLabel': {
              color: '#424242',
              fontSize: '0.875rem'
            },
            '& .MuiTablePagination-displayedRows': {
              color: '#424242',
              fontSize: '0.875rem'
            },
            '& .MuiTablePagination-select': {
              color: '#424242',
              fontSize: '0.875rem'
            },
            '& .MuiTablePagination-actions': {
              color: '#424242'
            }
          }}
        />
      </Box>

      {/* Add Dialog before the final closing tag */}
      <Dialog
        open={deactivateDialog}
        onClose={handleCancelDeactivate}
        PaperProps={{
          sx: {
            width: '450px',
            borderRadius: '4px',
            boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'rgba(0, 0, 0, 0.87)',
          fontWeight: 500,
          padding: '16px 24px',
          fontSize: '1.25rem'
        }}>
          Confirm Deactivation
        </DialogTitle>
        <DialogContent sx={{ padding: '20px 24px' }}>
          <Typography sx={{ 
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: '0.875rem',
            lineHeight: 1.43
          }}>
            This is a paid track. Are you sure you want to deactivate it?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          padding: '8px 24px'
        }}>
          <Button
            onClick={handleCancelDeactivate}
            sx={{
              color: 'rgba(0, 0, 0, 0.87)',
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 8px',
              minWidth: '64px',
              fontSize: '0.875rem'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeactivate}
            sx={{
              backgroundColor: '#f44336',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 8px',
              minWidth: '64px',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: '#d32f2f'
              }
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            minWidth: 'auto',
            backgroundColor: '#FF7043',
            color: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiAlert-root': {
            width: '100%',
            marginTop: '16px'
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              color: '#000000'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainContent>
  );
} 